<template>
  <div class="layout-sidebar-section">
    <div
      class="layout-sidebar-section-title"
      @click="$emit('toggleSection', 'objectSettingsSection')"
    >
      <strong>Object Settings</strong>
      <b-icon :icon="isOpen.objectSettingsSection ? 'chevron-up' : 'chevron-down'" />
    </div>
    <transition>
      <b-collapse :visible="isOpen.objectSettingsSection">
        <b-form-group
          inline
        >
          <label>
            Object Fill
            <b-tooltip
              target="object-fill-info-icon"
              placement="top"
            >
              To scale an image, set Object Fill to None
            </b-tooltip>
            <b-icon-info-circle
              id="object-fill-info-icon"
              variant="primary"
              class="ml-1"
            />
          </label>
          <b-form-select
            v-model="objectFillSettings.align_to_fill"
            :options="objectFitAndFillOptions.objectFillOptions"
            text-field="name"
            value-field="value"
            @change="onChange('objectFillSettings')"
          />
          <b-form-checkbox 
            v-model="objectFillCropMode" 
            name="crop-mode" 
            switch
            @change="onChange('objectFillSettings')"
          >
            Crop
          </b-form-checkbox>
        </b-form-group>
        <b-form-group
          inline
        >
          <label>
            Object Fit
            <b-tooltip
              target="object-fit-info-icon"
              placement="top"
            >
              To scale an image, set Object Fit to None
            </b-tooltip>
            <b-icon-info-circle
              id="object-fit-info-icon"
              variant="primary"
              class="ml-1"
            />
          </label>
          <b-form-select
            v-model="objectFitSettings.align_to_fit"
            :options="objectFitAndFillOptions.objectFitOptions"
            text-field="name"
            value-field="value"
            @change="onChange('objectFitSettings')"
          />
        </b-form-group>
      </b-collapse>
    </transition>
  </div>
</template>

<script>
import {isString} from "lodash";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import {mapGetters} from "vuex";
import {BACKGROUND_THEME_IMAGE_PREFIX_ID, ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID} from "@frontend/constants/templates-field-prefixes";
import {deepCopy} from "@frontend/utils";

export default {
  name: 'ObjectFillFit',
  props: {
    target: {
      type: fabric.Object,
      default: () => {}
    },
    isOpen: {
      type: Object,
      default: () => ({
        objectSettingsSection: true,
      }),
    },
  },
  data() {
    return {
      objectFillSettings: {
        width_to_fill: 0,
        height_to_fill: 0,
        align_to_fill: ''
      },
      objectFitSettings: {
        width_to_fit: 0,
        height_to_fit: 0,
        align_to_fit: ''
      },
      objectFitAndFillOptions: {
        objectFitOptions: [
          {
            "value": "",
            "name": "none"
          },
          {
            "value": "left,top",
            "name": "┍ left, top"
          },
          {
            "value": "center,top",
            "name": "┯ center, top"
          },
          {
            "value": "right,top",
            "name": "┑right, top"
          },
          {
            "value": "left,center",
            "name": "┡ left, center"
          },
          {
            "value": "center,center",
            "name": "╇ center, center"
          },
          {
            "value": "right,center",
            "name": "┥ right, center"
          },
          {
            "value": "left,bottom",
            "name": "┕ left, bottom"
          },
          {
            "value": "center,bottom",
            "name": "┻ center, bottom"
          },
          {
            "value": "right,bottom",
            "name": "┙right, bottom"
          }
        ],
        objectFillOptions: [
          {
            "value": "",
            "name": "none"
          },
          {
            "value": "left,top",
            "name": "┍ left, top"
          },
          {
            "value": "center,top",
            "name": "┯ center, top"
          },
          {
            "value": "right,top",
            "name": "┑right, top"
          },
          {
            "value": "left,center",
            "name": "┡ left, center"
          },
          {
            "value": "center,center",
            "name": "╇ center, center"
          },
          {
            "value": "right,center",
            "name": "┥ right, center"
          },
          {
            "value": "left,bottom",
            "name": "┕ left, bottom"
          },
          {
            "value": "center,bottom",
            "name": "┻ center, bottom"
          },
          {
            "value": "right,bottom",
            "name": "┙right, bottom"
          }
        ]
      },
      objectFillCropMode: true,
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    isValidFill() {
      const {width_to_fill, height_to_fill, align_to_fill} = this.objectFillSettings;
      return width_to_fill && height_to_fill && isString(align_to_fill) && align_to_fill.includes(',')
    },
    isValidFit() {
      const {width_to_fit, height_to_fit, align_to_fit} = this.objectFitSettings;
      return width_to_fit && height_to_fit && isString(align_to_fit) && align_to_fit.includes(',')
    },
  },
  watch: {
    target() {
      this.initData()
    }
  },
  mounted() {
   this.initData()
  },
  methods: {
    toggle () {
      this.target.set('locked', !this.target.locked)
      this.target.canvas.fire('locked:changed', this.target)
    },
    getSettingKeys() {
      const targetId = this.target?.id ?? '';
      const isAdditional = targetId.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID);

      return {
        fit: isAdditional ? 'add_bk_img_object_fit' : 'bk_img_object_fit',
        fill: isAdditional ? 'add_bk_img_object_fill' : 'bk_img_object_fill',
        prefix: isAdditional ? ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID : BACKGROUND_THEME_IMAGE_PREFIX_ID,
      };
    },
    initData() {
      const objectFitSettings = this.target?.objectFitSettings ?? {};
      const objectFillSettings = this.target?.objectFillSettings ?? {};
      this.objectFillSettings = {
        ...objectFillSettings,
        align_to_fill: objectFillSettings.align_to_fill 
          ? objectFillSettings.align_to_fill : '' 
      };
      this.objectFitSettings = {
        ...objectFitSettings, align_to_fit: objectFitSettings.align_to_fit 
          ? objectFitSettings.align_to_fit : ''
      };
      
      this.objectFillCropMode = Object.hasOwn(objectFillSettings, 'objectFillCrop') ? objectFillSettings.objectFillCrop : true;
    },
    async onChange(action) {
      const savedObjectId = this.target.id;
      const {
        updateLayoutChanges,
        setBackgroundImage,
        setAdditionalBackgroundImage,
        drawSmartObject,
        reorderObjectCanvas
      } = ShowPageScopeCollector.get([
        'updateLayoutChanges',
        'setBackgroundImage',
        'setAdditionalBackgroundImage',
        'drawSmartObject',
        'reorderObjectCanvas',
      ]);
      const { fit: fitKey, fill: fillKey, prefix } = this.getSettingKeys();
      switch (action) {
        case 'objectFillSettings':
          this.objectFitSettings.align_to_fit = '';
          break;
        case 'objectFitSettings':
          this.objectFillSettings.align_to_fill = '';
          break;
      }
      const index = this.target.id.split('_').reverse()[0];
      const dataObjectFill = this.selectedInstance.canvas.getObjects()
        .filter(object => object.id.startsWith(prefix))
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(object => object.objectFillSettings);
      const dataObjectFit = this.selectedInstance.canvas.getObjects()
        .filter(object => object.id.startsWith(prefix))
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(object => object.objectFitSettings);

      let objectFitData = [...dataObjectFit.map(deepCopy)]
        .map(item => {
          if (!item.align_to_fit) {
            item.align_to_fit = ''
          }
          return item;
        });

      if (Object.hasOwn(this.selectedInstance.data, fitKey)) {
        objectFitData = this.selectedInstance.data[fitKey];
      }
      
      objectFitData[index] ??= {};
      objectFitData[index]['align_to_fit'] = this.objectFitSettings.align_to_fit;
      let objectFillData = [...dataObjectFill.map(deepCopy)]
        .map(item => {
          if (!item.align_to_fill) {
            item.align_to_fill = ''
          }
          return item;
        });

      if (Object.hasOwn(this.selectedInstance.data, fillKey)) {
        objectFillData = this.selectedInstance.data[fillKey];
      }

      objectFillData[index] ??= {};
      objectFillData[index]['align_to_fill'] = this.objectFillSettings.align_to_fill;
      objectFillData[index]['objectFillCrop'] = this.objectFillCropMode;
      // Keep instance data in sync with layout changes so downstream consumers see updates immediately
      this.selectedInstance.data[fillKey] = objectFillData;
      this.selectedInstance.data[fitKey] = objectFitData;

      updateLayoutChanges(this.selectedInstance.id, fillKey, objectFillData);
      updateLayoutChanges(this.selectedInstance.id, fitKey, objectFitData);

      if (prefix === ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID) {
        // Additional layers redraw in isolation; no need to touch the primary background stack
        await setAdditionalBackgroundImage(this.selectedInstance);
      } else {
        // Refresh primary backgrounds, then re-layer additional ones so both sets stay in sync
        // If we don't do this, the additional backgrounds will be vanished when the primary background is changed
        await setBackgroundImage(this.selectedInstance);
        await setAdditionalBackgroundImage(this.selectedInstance);
      }

      drawSmartObject({...this.selectedInstance, fields: this.selectedInstance.template.fields});
      reorderObjectCanvas(this.selectedInstance.canvas);

      const updatedObject = this.selectedInstance.canvas.getObjects().find(obj => obj.id === savedObjectId);
      if (updatedObject) {
        this.selectedInstance.canvas.setActiveObject(updatedObject);
      }
      this.selectedInstance.canvas.requestRenderAll();
    },
  }
}
</script>

<style scoped lang="scss">
</style>
